import Cart from "cart-store";
import { formatMoney } from "utils";

/*
  Free shipping bar
  Uniq Creative, 14 Oct 2026: subscription (selling plan) lines ship free
  regardless of the spend threshold, so any cart containing one is treated
  as having reached the goal. Bar fills and the subscription message shows.
*/

const cartHasSubscription = (cart) =>
  Boolean(
    cart &&
      Array.isArray(cart.items) &&
      cart.items.some((item) => item && item.selling_plan_allocation)
  );

const setHidden = (element, hidden) => {
  if (!element) return;
  if (hidden) element.setAttribute("hidden", "");
  else element.removeAttribute("hidden");
};

class FreeShippingBar extends HTMLElement {
  #goalInterval = null;
  #onModalToggle;
  #modal;
  #animationTriggered;

  constructor() {
    super();
    this.#onModalToggle = this.#handleModalToggle.bind(this);
  }

  async connectedCallback() {
    this.amountToSpendElement = this.querySelector("amount-to-spend");
    this.amountLeftElement = this.querySelector("amount-left");
    this.moneyValueLeftElement = this.amountLeftElement.querySelector("money-value");
    this.minimumReachedElement = this.querySelector("minimum-reached");
    this.goalThresholdText = this.querySelector('[data-shipping-goal="threshold"]');
    this.goalSubscriptionText = this.querySelector('[data-shipping-goal="subscription"]');
    this.track = this.querySelector("free-shipping-bar-track");
    this.goalAnimation = null;
    this.#animationTriggered = false;
    this.minimumValue = Number(this.getAttribute("minimum-value"));
    this.value = Number(this.getAttribute("value"));
    this.subscription = this.hasAttribute("subscription");
    this.#modal = this.closest("modal-dialog, popup-dialog");
    this.#calculate();

    // Bars cloned from a <template> are rendered without the cart, so sync
    // them to the live cart state as soon as they connect.
    const currentCart = Cart.getState().cart;
    if (currentCart && typeof currentCart.total_price === "number") {
      const subscription = cartHasSubscription(currentCart);
      if (currentCart.total_price !== this.value || subscription !== this.subscription) {
        this.value = currentCart.total_price;
        this.subscription = subscription;
        this.#render();
      }
    }

    this.unsubscribe = Cart.subscribe((state) => {
      const cart = state.cart || {};
      const total = Number(cart.total_price) || 0;
      const subscription = cartHasSubscription(cart);

      if (this.value !== total || this.subscription !== subscription) {
        this.value = total;
        this.subscription = subscription;
        this.#render();
      }

      if (this.value > 0 && this.amountLeft === 0) {
        this.track.addEventListener(
          "transitionend",
          () => {
            setTimeout(() => {
              this.#playGoalAnimation();
            }, 250);
          },
          { once: true }
        );
      }
    });

    this.revealDelay = this.getAttribute("reveal-delay") || 0;

    if (this.#modal) {
      this.#modal.addEventListener("on:modal:opened", this.#onModalToggle);
      this.#modal.addEventListener("on:modal:closed", this.#onModalToggle);
    } else {
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setTimeout(() => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add("is-visible");
              setTimeout(() => {
                if (this.amountLeft === 0) this.#playGoalAnimation();
              }, 500);
              this.visibilityObserver.disconnect();
            }, this.revealDelay);
          });
        },
        { rootMargin: "0px", threshold: 0.95 }
      );
      this.visibilityObserver.observe(this);
    }

    const animationElement = this.querySelector("free-shipping-bar-goal-animation");
    if (animationElement) {
      const { DotLottie } = await import("dotlottie");
      const createAnimation = () => {
        this.goalAnimation = new DotLottie({
          canvas: animationElement.querySelector("canvas"),
          loop: false,
          autoplay: false,
          src: animationElement.getAttribute("data-url"),
        });
      };
      if (this.#modal) this.#modal.on("on:modal:opening", createAnimation, { once: true });
      else createAnimation();
    }
  }

  disconnectedCallback() {
    this.classList.remove("is-visible");
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.#modal) {
      this.#modal.removeEventListener("on:modal:opened", this.#onModalToggle);
      this.#modal.removeEventListener("on:modal:closed", this.#onModalToggle);
    }
    if (this.unsubscribe) this.unsubscribe();
    if (this.#goalInterval) clearInterval(this.#goalInterval);
  }

  #calculate() {
    const left = this.minimumValue - this.value;
    this.amountLeft = this.subscription || left <= 0 ? 0 : left;
    this.moneyLeft = formatMoney(this.amountLeft, window.theme.money_format).replace(/[.,]00$/, "");
    this.displacement =
      this.minimumValue > 0 ? Math.round((this.amountLeft / this.minimumValue) * 100) : 0;
  }

  #render() {
    this.setAttribute("value", this.value);
    this.toggleAttribute("subscription", this.subscription);
    this.#calculate();

    if (this.moneyValueLeftElement) this.moneyValueLeftElement.innerHTML = this.moneyLeft;

    // Subscription is the only reason shipping is free (under the threshold).
    const subscriptionOnly = this.subscription && this.value < this.minimumValue;

    setHidden(this.amountToSpendElement, this.value > 0 || this.subscription);
    setHidden(this.amountLeftElement, this.value === 0 || this.amountLeft === 0);
    setHidden(this.minimumReachedElement, this.amountLeft > 0);
    setHidden(this.goalThresholdText, subscriptionOnly);
    setHidden(this.goalSubscriptionText, !subscriptionOnly);

    this.track.style.setProperty("--displacement", `${this.displacement}%`);
  }

  #playGoalAnimation() {
    const animationElement = this.querySelector("free-shipping-bar-goal-animation");
    const alreadyShown = Boolean(localStorage.getItem("freeShippingAnimationShown"));
    if (!animationElement || alreadyShown || this.#animationTriggered) return;

    this.#animationTriggered = true;
    this.#goalInterval = setInterval(() => {
      if (this.goalAnimation && this.goalAnimation.isLoaded) {
        clearInterval(this.#goalInterval);
        this.#goalInterval = null;
        const canvas = this.querySelector("free-shipping-bar-goal-animation canvas");
        if (canvas.getAttribute("width") === "0") {
          canvas.setAttribute("width", 300);
          canvas.setAttribute("height", 300);
        }
        localStorage.setItem("freeShippingAnimationShown", true);
        this.goalAnimation.play();
      }
    }, 100);
  }

  #handleModalToggle(event) {
    if (event.type === "on:modal:opened") this.classList.add("is-visible");
    else if (event.type === "on:modal:closed") this.classList.remove("is-visible");
  }
}

customElements.define("free-shipping-bar", FreeShippingBar);
